import { Link } from 'react-router-dom';
import { Button } from '@voilajsx/uikit/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@voilajsx/uikit/card';
import { Progress } from '@voilajsx/uikit/progress';
import { BookOpen, Palette, Layout, FolderTree, Rocket, ArrowRight } from 'lucide-react';
import { TutorialLayout } from '../../../shared/components';
import { tutorialLevels, getTotalTutorialCount } from '../../../shared/config/tutorials';

const TutorialsIndex = () => {
  const levelIcons = {
    foundation: BookOpen,
    theming: Palette,
    layouts: Layout,
    architecture: FolderTree,
    production: Rocket,
  };

  return (
    <TutorialLayout levels={tutorialLevels}>
      <div className="space-y-8">
        {/* Hero */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-4">UIKit Tutorials</h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-4">
            {getTotalTutorialCount()} hands-on tutorials across 5 levels.
            Learn everything from basics to production deployment.
          </p>
          <div className="flex items-center justify-center gap-4">
            <div className="flex items-center gap-2 text-sm">
              <Progress value={0} className="w-32 h-2" />
              <span className="text-muted-foreground">0% complete</span>
            </div>
          </div>
        </div>

        {/* Levels */}
        <div className="space-y-6">
          {tutorialLevels.map((level, levelIndex) => {
            const Icon = levelIcons[level.id as keyof typeof levelIcons] || BookOpen;

            return (
              <Card key={level.id} className="overflow-hidden">
                <CardHeader className="bg-muted/30 pb-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                        <Icon className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{level.title}</CardTitle>
                        <CardDescription>{level.description}</CardDescription>
                      </div>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {level.pages.length} lessons
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="space-y-2">
                    {level.pages.map((page, pageIndex) => (
                      <Link
                        key={page.id}
                        to={page.path}
                        className="group flex items-center gap-3 p-3 rounded-lg border border-border hover:border-primary/50 hover:bg-muted/50 transition-colors"
                      >
                        <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary transition-colors">
                          {levelIndex + 1}.{pageIndex + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm truncate group-hover:text-primary transition-colors">
                            {page.title}
                          </div>
                        </div>
                        <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                      </Link>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Start CTA */}
        <div className="mt-8 text-center">
          <Link to="/tutorials/foundation/what-is-uikit">
            <Button size="lg" className="px-8">
              Start with Level 1
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </TutorialLayout>
  );
};

export default TutorialsIndex;
